import { TrendingUp, TrendingDown } from "lucide-react";
import { formatCurrency, formatPct } from "../../lib/format";

const Metric = ({ label, value, testid, emphasis }) => (
  <div className={emphasis ? "" : "rounded-md border border-white/10 bg-white/5 p-4"}>
    <div className="text-[11px] font-semibold uppercase tracking-wider text-white/60">{label}</div>
    <div
      className={`tabular mt-1 font-mono font-semibold ${emphasis ? "text-4xl sm:text-5xl" : "text-xl"}`}
      data-testid={testid}
    >
      {value}
    </div>
  </div>
);

export const ResultCard = ({ calc, results }) => {
  const isCod = calc.calcType === "cod";
  const profit = isCod ? results.netProfit : results.netProfit;
  const isLoss = profit < 0;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-slate-900 text-white shadow-sm">
      <div className="border-b border-white/10 px-6 py-4">
        <div className="flex items-center gap-2 text-sm font-medium text-white/70">
          {isLoss ? <TrendingDown className="h-4 w-4 text-red-400" /> : <TrendingUp className="h-4 w-4 text-emerald-400" />}
          {isCod ? "Batch result" : "Per-order result"}
        </div>
      </div>

      <div className="px-6 py-6">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-white/60">
          {isCod ? "Net profit (all orders)" : "Net profit"}
        </div>
        <div
          className={`tabular mt-1 font-mono text-4xl font-bold sm:text-5xl ${isLoss ? "text-red-400" : "text-emerald-400"}`}
          data-testid="result-net-profit"
        >
          {formatCurrency(profit)}
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3">
          {!isCod && (
            <>
              <Metric label="Profit margin" value={formatPct(results.marginPct)} testid="result-margin" />
              <Metric label="ROI" value={formatPct(results.roiPct)} testid="result-roi" />
              <Metric label="Break-even price" value={formatCurrency(results.breakEvenPrice)} testid="result-breakeven" />
              <Metric label="Max ad cost / order" value={formatCurrency(results.maxAdCost)} testid="result-maxad" />
              {calc.isFeeFocus && (
                <>
                  <Metric label="Total fees" value={formatCurrency(results.feeCosts)} testid="result-fees" />
                  <Metric label="Net payout" value={formatCurrency(results.revenue - results.feeCosts)} testid="result-payout" />
                </>
              )}
            </>
          )}
          {isCod && (
            <>
              <Metric label="Profit / delivered order" value={formatCurrency(results.profitPerDeliveredOrder)} testid="result-per-delivered" />
              <Metric label="Profit margin" value={formatPct(results.marginPct)} testid="result-margin" />
              <Metric label="Total RTO loss" value={formatCurrency(results.totalRtoLoss)} testid="result-rto-loss" />
              <Metric label="Delivered / RTO orders" value={`${Math.round(results.deliveredOrders)} / ${Math.round(results.rtoOrders)}`} testid="result-delivered" />
            </>
          )}
        </div>
      </div>

      <div className={`px-6 py-4 text-sm ${isLoss ? "bg-red-500/15 text-red-200" : "bg-emerald-500/15 text-emerald-100"}`} data-testid="result-interpretation">
        {isCod ? (
          isLoss ? (
            <>At a {formatPct(0)} you are losing money overall. Your RTO loss is {formatCurrency(results.totalRtoLoss)} — reduce RTO or raise your price to turn this positive.</>
          ) : (
            <>You keep {formatCurrency(results.profitPerDeliveredOrder)} per delivered order after spreading {formatCurrency(results.totalRtoLoss)} of RTO losses across the batch.</>
          )
        ) : isLoss ? (
          <>You lose {formatCurrency(Math.abs(profit))} on every order at this price. Your break-even price is {formatCurrency(results.breakEvenPrice)}.</>
        ) : (
          <>You earn {formatCurrency(profit)} per sale after the costs you entered. Your break-even selling price is {formatCurrency(results.breakEvenPrice)}.</>
        )}
      </div>
    </div>
  );
};
